// YERİ: src/app/domains/site/sections/inventory.sections.ts
//
// ENVANTER BÖLÜMLERİ — emlak ilanları, satılık/kiralık araçlar, depo
// birimleri, pet pansiyon birimleri, tarım ürünleri.
//
// Hepsinin ortak yanı: ziyaretçi "ne var, ne kadar, boş mu?" diye bakıyor.
//
// ── GİZLİLİK (sunucuda uygulanıyor, burada tekrar edilmiyor) ──────────────
//   • Araçta plaka/şasi YOK ve servisteki MÜŞTERİ ARACI hiç gelmiyor.
//   • Emlakta tam adres YOK; ilçe var.
//   • Depoda birim NUMARASI yok; kaç birim boş, o var.
//   • Tarımda ekili alan ve rekolte yok.

import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import { paraBirimi, duvarTarihi } from './community.sections';
import {
  TenantWebService, TenantProperty, TenantVehicle,
  TenantStorageUnitType, TenantBoardingUnit, TenantCrop,
} from '../core/tenant-web.service';

// ══════════════════════════════════════════════════════════════════════════
// EMLAK İLANLARI
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-property-listings',
  standalone: true,
  template: `
    @if (ilan().length) {
    <section id="listings" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (i of ilan(); track i.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
              <div class="flex items-start justify-between gap-2">
                <div class="flex flex-wrap gap-1.5">
                  @if (i.purpose) {
                    <span class="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" style="background:var(--site-primary)">{{ amac(i) }}</span>
                  }
                  @if (i.propertyType) {
                    <span class="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{{ i.propertyType }}</span>
                  }
                </div>
                <!-- Teklif alınmış ilan gizlenmiyor ama işaretleniyor:
                     ziyaretçi hâlâ ilgilenebilir, sadece durumu bilmeli. -->
                @if (quoteReceived(i)) {
                  <span class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{{ text.t('pr.underOffer') }}</span>
                }
              </div>

              <h3 class="mt-3 text-lg font-bold text-neutral-900 dark:text-white">{{ i.title }}</h3>
              @if (konum(i)) {
                <p class="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">{{ konum(i) }}</p>
              }
              @if (i.price > 0) {
                <p class="mt-3 text-2xl font-bold tabular-nums" style="color:var(--site-primary)">{{ fiyat(i) }}</p>
              } @else {
                <p class="mt-3 text-sm text-neutral-500 dark:text-neutral-400">{{ text.t('pr.askPrice') }}</p>
              }

              <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-600 dark:text-neutral-400">
                @if (i.areaSqm) { <span>{{ i.areaSqm }} m²</span> }
                @if (i.roomLayout) { <span>{{ i.roomLayout }}</span> }
              </div>

              @if (i.description) {
                <p class="mt-3 line-clamp-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ i.description }}</p>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class PropertyListingsSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected ilan = signal<TenantProperty[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    const amac = String(cfg(this.config(), 'purpose', ''));
    const qty = Number(cfg(this.config(), 'count', 6)) || 6;
    this.svc.getProperties(id, amac, qty).subscribe({
      next: (l) => this.ilan.set(l),
      error: () => this.ilan.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.portfoy')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.guncelIlanlar')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected fiyat = (i: TenantProperty) => paraBirimi(i.price, i.currency, this.text.lang(), this.text.currencyKey());
  protected quoteReceived = (i: TenantProperty) => (i.status || '').toLowerCase() === 'under_offer';

  protected amac = (i: TenantProperty) =>
    (i.purpose || '').toLowerCase() === 'rent' ? this.text.t('pr.forRent') : this.text.t('pr.forSale');

  /** İlçe + şehir; tam adres sunucudan HİÇ gelmiyor. */
  protected konum = (i: TenantProperty) => [i.district, i.city].filter(Boolean).join(', ');
}

// ══════════════════════════════════════════════════════════════════════════
// SATILIK / KİRALIK ARAÇLAR
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-vehicle-showcase',
  standalone: true,
  template: `
    @if (tool().length) {
    <section id="vehicles" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          @for (a of tool(); track a.id) {
            <div class="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
              <span class="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" style="background:var(--site-primary)">{{ amac(a) }}</span>
              <h3 class="mt-3 font-bold text-neutral-900 dark:text-white">{{ a.make }} {{ a.model }}</h3>
              <ul class="mt-2 space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
                @if (a.year) { <li>{{ a.year }} {{ text.t('vh.model') }}</li> }
                @if (a.mileage != null) { <li class="tabular-nums">{{ km(a) }} km</li> }
                @if (a.color) { <li>{{ a.color }}</li> }
              </ul>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class VehicleShowcaseSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected tool = signal<TenantVehicle[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    const amac = String(cfg(this.config(), 'purpose', ''));
    const qty = Number(cfg(this.config(), 'count', 8)) || 8;
    this.svc.getVehicles(id, amac, qty).subscribe({
      next: (l) => this.tool.set(l),
      error: () => this.tool.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.araclar')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.satilikVeKiralikAraclar')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected amac = (a: TenantVehicle) =>
    (a.purpose || '').toLowerCase() === 'rental' ? this.text.t('vh.rental') : this.text.t('vh.sale');

  /** Sabit 'tr-TR' YOK: binlik ayracı sitenin diline göre. */
  protected km = (a: TenantVehicle) => (a.mileage ?? 0).toLocaleString(this.text.lang());
}

// ══════════════════════════════════════════════════════════════════════════
// DEPO BİRİMLERİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-storage-units',
  standalone: true,
  template: `
    @if (tip().length) {
    <section id="units" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          @for (t of tip(); track t.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
              <h3 class="font-bold text-neutral-900 dark:text-white">{{ t.name }}</h3>
              @if (t.category) {
                <span class="mt-0.5 text-xs uppercase tracking-wide" style="color:var(--site-accent)">{{ t.category }}</span>
              }
              <p class="mt-3 text-sm text-neutral-600 dark:text-neutral-400 tabular-nums">{{ olculer(t) }}</p>
              @if (t.monthlyRate > 0) {
                <p class="mt-3 text-2xl font-bold tabular-nums" style="color:var(--site-primary)">
                  {{ fiyat(t) }}
                  <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">{{ text.t('st.perMonth') }}</span>
                </p>
              } @else {
                <p class="mt-3 text-sm text-neutral-500 dark:text-neutral-400">{{ text.t('pr.askPrice') }}</p>
              }
              @if (t.description) {
                <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ t.description }}</p>
              }
              <!-- Kaç birim BOŞ; hangi birim olduğu değil. -->
              <p class="mt-4 text-sm font-semibold" [class.text-emerald-600]="t.availableCount > 0" [class.text-neutral-400]="t.availableCount === 0">
                {{ t.availableCount > 0 ? (t.availableCount + ' ' + text.t('st.available')) : text.t('st.full') }}
              </p>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class StorageUnitsSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected tip = signal<TenantStorageUnitType[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getStorageUnitTypes(id).subscribe({
      next: (l) => this.tip.set(l),
      error: () => this.tip.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Depo'));
  protected title = computed(() => cfg(this.config(), 'title', 'Birim Tipleri ve Fiyatlar'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected fiyat = (t: TenantStorageUnitType) => paraBirimi(t.monthlyRate, t.currency, this.text.lang(), this.text.currencyKey());

  /** "3 × 3 m" ya da yükseklik varsa "3 × 3 × 2.5 m". */
  protected olculer = (t: TenantStorageUnitType): string => {
    const p = [t.width, t.depth];
    if (t.height != null) p.push(t.height);
    return p.join(' × ') + ' m';
  };
}

// ══════════════════════════════════════════════════════════════════════════
// PET PANSİYON BİRİMLERİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-boarding-units',
  standalone: true,
  template: `
    @if (birim().length) {
    <section id="boarding" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          <p class="mt-3 text-sm text-neutral-500 dark:text-neutral-400">{{ ozet() }}</p>
        </div>

        <div class="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          @for (b of birim(); track b.id) {
            <div class="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
              <div class="min-w-0">
                <h3 class="truncate font-semibold text-neutral-900 dark:text-white">{{ b.name }}</h3>
                <p class="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                  {{ b.unitType }}@if (b.zone) {<span> · {{ b.zone }}</span>} · {{ b.capacity }} {{ text.t('bd.capacity') }}
                </p>
              </div>
              <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold"
                    [class]="b.isAvailable
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'">
                {{ b.isAvailable ? text.t('bd.free') : text.t('bd.occupied') }}
              </span>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class BoardingUnitsSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected birim = signal<TenantBoardingUnit[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getBoardingUnits(id).subscribe({
      next: (l) => this.birim.set(l),
      error: () => this.birim.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Pansiyon'));
  protected title = computed(() => cfg(this.config(), 'title', 'Konaklama Birimlerimiz'));

  /** "5 birimden 3'ü boş" — ziyaretçinin ilk sorusu bu. */
  protected ozet = computed(() => {
    const all = this.birim();
    const empty = all.filter(b => b.isAvailable).length;
    return `${all.length} ${this.text.t('bd.unitsOf')} ${empty} ${this.text.t('bd.freeNow')}`;
  });
}

// ══════════════════════════════════════════════════════════════════════════
// TARIM ÜRÜNLERİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-produce-list',
  standalone: true,
  template: `
    @if (product().length) {
    <section id="produce" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          @for (u of product(); track u.id) {
            <div class="rounded-xl border border-neutral-200 bg-white p-5 text-center dark:border-neutral-800 dark:bg-neutral-950">
              <h3 class="font-bold text-neutral-900 dark:text-white">{{ u.cropName }}</h3>
              @if (u.season) {
                <p class="mt-1 text-xs uppercase tracking-wide" style="color:var(--site-accent)">{{ u.season }}</p>
              }
              @if (u.expectedHarvestDate) {
                <p class="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
                  {{ text.t('cr.harvest') }}<br /><strong>{{ hasat(u) }}</strong>
                </p>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class ProduceListSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected product = signal<TenantCrop[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    const qty = Number(cfg(this.config(), 'count', 8)) || 8;
    this.svc.getCrops(id, qty).subscribe({
      next: (l) => this.product.set(l),
      error: () => this.product.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.urunlerimiz')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.neYetistiriyoruz')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected hasat = (u: TenantCrop) => duvarTarihi(u.expectedHarvestDate, this.text.lang(), false);
}
